import { Component, OnInit, ViewChild, ElementRef, AfterViewInit, Inject, HostListener } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { NotificationsService } from 'angular2-notifications';
import { ComponentVM, DrawShape, GenericIdName, Locations, Points, ResponseMessage } from '../setup-implement/setup-implement.model';
import { SetupImplementService } from '../setup-implement/setup-implement.service';

@Component({
  selector: 'app-schematic-comps-marking',
  templateUrl: './schematic-comps-marking.component.html',
  styleUrls: ['./schematic-comps-marking.component.css']
})
export class SchematicCompsMarkingComponent
  implements OnInit, AfterViewInit {

  @ViewChild('image') imageRef!: ElementRef<HTMLImageElement>;
  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  ctx!: CanvasRenderingContext2D;

  shapes: DrawShape[] = [];
  activeShape: DrawShape | null = null;
  currentPoints: Points[] = [];

  mousePos: Points | null = null;
  isDrawing = false;

  readonly CLOSE_DISTANCE = 10;

  schematicData: string;
  implementId: number;
  componentList: ComponentVM[];
  loading: boolean = false;

  componentTypeList: GenericIdName[] = [];
  componentType: number = 0;
  mouseIn: boolean = false;

  zoom = 1;
  readonly MIN_ZOOM = 0.5;
  readonly MAX_ZOOM = 3;
  readonly ZOOM_STEP = 0.1;

  panX = 0;
  panY = 0;
  readonly PAN_STEP = 20;

  constructor(private _sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _implementService: SetupImplementService,
    public dialogRef: MatDialogRef<SchematicCompsMarkingComponent>,
    private _notify: NotificationsService
  ) { }

  ngOnInit(): void {
    this.schematicData = this.data.schematic;
    this.implementId = this.data.implementId;
    this.loadComponentList();
    if (this.implementId > 0) {
      this.loadComponentTypes();
      this.loadSavedShapes();
    }
  }

  ngAfterViewInit(): void {
    this.drawOriginalImage();
  }

  drawOriginalImage() {
    const img = this.imageRef.nativeElement;
    img.onload = () => {
      const canvas = this.canvasRef.nativeElement;
      //canvas.width = img.clientWidth;
      //canvas.height = img.clientHeight;
      //canvas.style.width = img.clientWidth + 'px';
      //canvas.style.height = img.clientHeight + 'px';
      canvas.width = 640;
      canvas.height = img.clientHeight;
      canvas.style.width = '640px';
      canvas.style.height = img.clientHeight + 'px';
      this.ctx = canvas.getContext('2d')!;
      this.redraw();
    };
   
  }

  @HostListener('document:click', ['$event'])

  onDocumentClick(event: MouseEvent): void {
    if (!this.isDrawing) return;
    const canvasEl = this.canvasRef?.nativeElement;
    if (!canvasEl) return;
    const clickedInsideCanvas = canvasEl.contains(event.target as Node);
    if (!clickedInsideCanvas) {
      this.currentPoints = [];
      this.mousePos = null;
      this.isDrawing = false;
      this.redraw();
    }
  }

  //getPos(event: MouseEvent): Point {
  //  const canvas = this.canvasRef.nativeElement;
  //  const rect = canvas.getBoundingClientRect();
  //  return {
  //    x: parseFloat((event.clientX - rect.left).toFixed(2)),
  //    y: parseFloat((event.clientY - rect.top).toFixed(2))
  //  };
  //}

  getPos(event: MouseEvent): Points {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();

    const x = (event.clientX - rect.left) / this.zoom;
    const y = (event.clientY - rect.top) / this.zoom;

    return {
      x: parseFloat(x.toFixed(2)),
      y: parseFloat(y.toFixed(2))
    };
  }


  onCanvasClick(event: MouseEvent): void {
    event.stopPropagation();
    const pos = this.getPos(event);
    if (!this.isDrawing) {
      this.currentPoints = [pos];
      this.isDrawing = true;
      this.setCursor('drawing');
      //if (this.activeShape) {
      //  this.activeShape.points = this.currentPoints;
      //}
      this.redraw();
      return;
    }

    if (this.currentPoints.length >= 3 && this.isNearStart(pos)) {
      const start = this.currentPoints[0];
      this.currentPoints.push({
        x: start.x,
        y: start.y
      });
      this.finishShape(true);
      return;
    }
    this.currentPoints.push(pos);
    this.redraw();
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.isDrawing) return;
    const pos = this.getPos(event);
    this.mousePos = pos;
    this.redraw();
  }

  onCanvasEnter(): void {
    this.mouseIn = true;
    this.setCursor('drawing');
    //if (this.isDrawing) {
    //  this.setCursor('drawing');
    //} else {
    //  this.setCursor('default');
    //}
  }

  onCanvasLeave(): void {
    this.setCursor('default');
    if (this.mouseIn) {
      this.mouseIn = false;
      if (this.isDrawing) {
        this.currentPoints = [];
        this.mousePos = null;
        this.isDrawing = false;
        this.redraw();
      }
    }
  }

  finishShape(alreadyClosed: boolean = false): void {
    if (this.currentPoints.length < 3) return;
    if (!alreadyClosed) {
      this.currentPoints.push({ ...this.currentPoints[0] });
    }
    if (!this.activeShape) {
      this.shapes.push({
        id: this.shapes.length + 1,
        componentId: null,
        points: [...this.currentPoints],
        locations: [],
        newLocation: '',
        componentGroupNumber: 1
      });
    } else {
      this.activeShape.points = [...this.currentPoints];
    }
    this.currentPoints = [];
    this.mousePos = null;
    this.isDrawing = false;
    this.activeShape = null;
    this.redraw();
  }

  isNearStart(p: Points): boolean {
    if (!this.isDrawing || this.currentPoints.length < 3) {
      return false;
    }
    const start = this.currentPoints[0];
    const dx = p.x - start.x;
    const dy = p.y - start.y;
    return Math.sqrt(dx * dx + dy * dy) < this.CLOSE_DISTANCE;
  }

  redraw(): void {
    if (!this.ctx) return;

    const canvas = this.canvasRef.nativeElement;

    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);

    //this.ctx.scale(this.zoom, this.zoom);

    // Draw saved shapes
    this.shapes.forEach(shape => {
      if (shape.points.length > 0) {
        this.ctx.strokeStyle = '#BFEE90';
        this.ctx.lineWidth = 2;
        this.drawShape(shape.points, true);
      }
    });

    // Draw active shape
    if (this.currentPoints.length > 0) {
      this.ctx.strokeStyle = '#BFEE90';
      this.ctx.lineWidth = 2;
      this.drawShape(this.currentPoints, false);

      if (this.mousePos) {
        const last = this.currentPoints[this.currentPoints.length - 1];
        this.ctx.beginPath();
        this.ctx.moveTo(last.x, last.y);
        this.ctx.lineTo(this.mousePos.x, this.mousePos.y);
        this.ctx.strokeStyle = 'red';
        this.ctx.lineWidth = 2;
        this.ctx.stroke();
      }
    }
  }


  drawShape(points: Points[], closed: boolean): void {
    this.ctx.beginPath();
    this.ctx.moveTo(points[0].x, points[0].y);
    for (let i = 1; i < points.length; i++) {
      this.ctx.lineTo(points[i].x, points[i].y);
    }
    if (closed) {
      this.ctx.closePath();
    }
    this.ctx.stroke();
    this.ctx.fillStyle = 'rgba(255, 255, 0, 0.1)';
    this.ctx.fill();
  }

  loadComponentList() {
    this._implementService.ReturnGETSchematicComponents(this.implementId, this.data.schematicId).subscribe(result => {
      this.componentList = result
    }, error => { console.log(error); });
  }

  addLocation(shape: DrawShape) {
    if (shape.newLocation && shape.newLocation.trim()) {
      const newLocation: Locations = {
        Id: 0, 
        ComponentId: shape.componentId,
        LocationName: shape.newLocation.trim(),
        ComponentGroupNumber: shape.componentGroupNumber
      };
      shape.locations.push(newLocation);
      shape.newLocation = '';
    }
  }

  removeLocation(shape: DrawShape, index: number) {
    shape.locations.splice(index, 1);
  }

  editLocation(shape: DrawShape, index: number) {
    shape.locations.splice(index, 1);
  }

  ClearAllXY(shape: DrawShape) {
    if (shape != null) {
      shape.points = [];
    }
    this.activeShape = shape;
    this.currentPoints = [];
    this.isDrawing = true;
    this.mousePos = null;
    this.redraw();
  }

  isComponentUsed(componentId: number, currentShape: DrawShape): boolean {
    return this.shapes.some(
      s => s !== currentShape && s.componentId === componentId
    );
  }

  SetComponentGroupNumber(componentId: number, currentShape: DrawShape) {
    const existingShapes = this.shapes.filter(
      s => s !== currentShape && s.componentId === componentId
    );

    if (existingShapes.length === 0) {
      // First time selecting this component → default group 1
      currentShape.componentGroupNumber = 1;
    } else {
      // Increment group number based on last group used
      const maxGroup = Math.max(...existingShapes.map(s => s.componentGroupNumber || 1));
      currentShape.componentGroupNumber = maxGroup + 1;
    }
  }


  loadComponentTypes() {
    this._implementService.GetComponentTypeListForSelectedImplement(this.implementId).subscribe(r => {
      this.componentTypeList = r;
      this.GetSchematicComponentTypeViewModelList();
    });
  }

  ComponentTypeVM: any[] = [0];
  ComponentTypeVMForm: FormControl = new FormControl(this.ComponentTypeVM);
  GetSchematicComponentTypeViewModelList() {
    this.ComponentTypeVM = [];
    console.log("this.schematic.Id:", this.data.schematicId);
    this._implementService.GetSchematicComponentTypeViewModelList(this.data.schematicId, this.implementId).subscribe(res => {
      res.forEach(x => {
        this.ComponentTypeVM.push(x.Id);
      });
      this.ComponentTypeVMForm = new FormControl(this.ComponentTypeVM);
    });
  }

  loadSavedShapes(): void {
    this._implementService
      .getSchematicComponentsRecognitionsDetails(
        this.data.schematicId,
        this.implementId
      )
      .subscribe({
        next: (res: DrawShape[]) => {
          this.shapes = res.map((s, index) => ({
            id: index + 1,
            componentId: s.componentId,
            componentGroupNumber: s.componentGroupNumber,
            points: s.points ?? [],
            locations: s.locations ?? [],
            newLocation: ''
          }));

          this.redraw();
        },
        error: err => {
          console.error('Failed to load shapes', err);
        }
      });
  }

  saveAllShapes() {
    //const validShapes = this.shapes.filter(s =>
    //  (s.points && s.points.length > 0) ||
    //  (s.locations && s.locations.length > 0)
    //);
    //if (validShapes.length === 0) {
    //  this._notify.alert('No valid components to save.');
    //  return;
    //}
    const hasNullComponent = this.shapes.some(
      s => s.componentId == null && (s.points.length > 0 || s.locations.length > 0)
    );
    if (hasNullComponent) {
      this._notify.alert('Please select a component for all valid shapes before saving.');
      return;
    }
    this.loading = true;
    this._implementService.saveShapes(this.shapes).subscribe(
      (res: ResponseMessage) => {
        if (res.success) {
          this._notify.success(res.message);
        } else {
          this._notify.error(res.message);
        }
        this.loading = false;
      },
      (err) => {
        console.error(err);
        this._notify.error('Error while saving.');
        this.loading = false;
      }
    );
  }


  saveUpdateComponentType(ComponentTypeVM) {
    this.ComponentTypeVM = this.ComponentTypeVMForm.value;
    console.log(this.data.schematic.Id, this.componentType);
    const schematicComponentTypeModelsList: any[] = [];
    for (let items of this.ComponentTypeVM) {
      var x = {
        SchematicId: this.data.schematicId,
        componentTypeId: items,
        ImplementId: this.implementId
      };
      schematicComponentTypeModelsList.push(x)
    }
    this._implementService.saveUpdateComponentType(schematicComponentTypeModelsList).subscribe(r => {
      if (r) {
        this._notify.success("Component type mapping with the schematic was completed successfully.");
        this.loadComponentList();
      } else {
        this._notify.error('Error while saving.');
      }
    })
  }

  updateLocation(location) {
    this._implementService.saveShapeLocation(location).subscribe(
      (res: ResponseMessage) => {
        if (res.success) {
          this._notify.success(res.message);
        } else {
          this._notify.error(res.message);
        }
      },
      (err) => {
        console.error(err);
        this._notify.error('Error while saving.');
      }
    );
  }

  onCloseClick(): void {
    this.dialogRef.close(true);
  }

  sanitize(image: string) {
    return this._sanitizer.bypassSecurityTrustResourceUrl('data:image/*;base64,' + image);
  }

  setCursor(mode: 'default' | 'drawing' | 'panning') {
    const canvas = this.canvasRef.nativeElement;
    //console.log("switch (mode)", mode);
    switch (mode) {
      case 'drawing':
        canvas.style.cursor = 'crosshair';
        break;
      case 'panning':
        canvas.style.cursor = 'grab';
        break;
      default:
        canvas.style.cursor = 'default';
    }
  }

  get transformStyle(): string {
    return `translate(${this.panX}px, ${this.panY}px) scale(${this.zoom})`;
  }

  //Zoom functions
  zoomIn() {
    this.zoom = Math.min(this.MAX_ZOOM, this.zoom + this.ZOOM_STEP);
    this.redraw();
  }

  zoomOut() {
    this.zoom = Math.max(this.MIN_ZOOM, this.zoom - this.ZOOM_STEP);
    this.redraw();
  }

  panLeft() {
    this.panX += this.PAN_STEP;
    this.redraw();
  }

  panRight() {
    this.panX -= this.PAN_STEP;
    this.redraw();
  }

  panUp() {
    this.panY += this.PAN_STEP;
    this.redraw();
  }

  panDown() {
    this.panY -= this.PAN_STEP;
    this.redraw();
  }

  resetCanvas() {
    this.drawOriginalImage();
    // Reset zoom/pan
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.redraw();
  }

  //

}
